'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { Post } from '@/lib/posts';
import { DEFAULT_COVER_IMAGE } from '@/lib/constants';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function HeroSlider({ posts }: { posts: Post[] }) {
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [textColors, setTextColors] = useState<('light' | 'dark')[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % posts.length);
  }, [posts.length]);

  const prevSlide = useCallback(() => {
    setCurrent((prev) => (prev === 0 ? posts.length - 1 : prev - 1));
  }, [posts.length]);

  // Analyze image brightness to determine text color
  const analyzeImageBrightness = useCallback((imageUrl: string, index: number) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = imageUrl;

    img.onload = () => {
      if (!canvasRef.current) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set canvas size to a small sample for performance
      canvas.width = 100;
      canvas.height = 100;

      // Draw image on canvas
      ctx.drawImage(img, 0, 0, 100, 100);

      // Get image data from the left portion where text appears
      const imageData = ctx.getImageData(0, 0, 50, 100);
      const data = imageData.data;

      // Calculate average brightness
      let brightness = 0;
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        // Using perceived brightness formula
        brightness += (0.299 * r + 0.587 * g + 0.114 * b);
      }
      brightness = brightness / (data.length / 4);

      // If brightness > 128, use dark text; otherwise use light text
      setTextColors(prev => {
        const newColors = [...prev];
        newColors[index] = brightness > 128 ? 'dark' : 'light';
        return newColors;
      });
    };

    img.onerror = () => {
      // Default to light text on error
      setTextColors(prev => {
        const newColors = [...prev];
        newColors[index] = 'light';
        return newColors;
      });
    };
  }, []);

  // Analyze all images on mount or use manual override
  useEffect(() => {
    posts.forEach((post, index) => {
      // Check if there's a manual textColor override in frontmatter
      if (post.frontmatter.textColor === 'dark' || post.frontmatter.textColor === 'light') {
        setTextColors(prev => {
          const newColors = [...prev];
          newColors[index] = post.frontmatter.textColor as 'light' | 'dark';
          return newColors;
        });
      } else {
        // Auto-detect from image
        const imageUrl = post.frontmatter.coverImage || DEFAULT_COVER_IMAGE;
        analyzeImageBrightness(imageUrl, index);
      }
    });
  }, [posts, analyzeImageBrightness]);

  // Autoplay
  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide]);

  if (!posts || posts.length === 0) return null;

  const currentTextColor = textColors[current] || 'light';

  return (
    <div
      className="relative w-full h-[70vh] overflow-hidden bg-white group mb-20"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Slides */}
      {posts.map((post, index) => (
        <div
          key={post.slug}
          className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
            index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Background Image - Full Bleed with Ken Burns Effect */}
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={post.frontmatter.coverImage || DEFAULT_COVER_IMAGE}
              alt={post.frontmatter.title}
              className={`w-full h-full object-cover transition-transform duration-[8000ms] ease-out ${
                index === current ? 'scale-105' : 'scale-100'
              }`}
              onError={(e) => {
                e.currentTarget.src = DEFAULT_COVER_IMAGE;
              }}
            />
            {/* Very Subtle Overlay - Leica Style */}
            <div className="absolute inset-0 bg-black/20" />
          </div>

          {/* Content - Left Aligned & Centered Vertically with Stagger Animation */}
          <div className="absolute inset-0 flex items-center px-8 md:px-12 lg:px-16 z-20">
            <div className="max-w-2xl space-y-4">
              {/* Metadata Label - Small Uppercase with Fade In */}
              <div
                className={`flex items-center space-x-3 transition-all duration-700 ease-out ${
                  index === current
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                } ${currentTextColor === 'light' ? 'text-white' : 'text-[#151515]'}`}
                style={{ transitionDelay: index === current ? '200ms' : '0ms' }}
              >
                <time className="text-[10px] font-normal tracking-[0.2em] uppercase opacity-90">
                  {new Date(post.frontmatter.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    timeZone: 'UTC'
                  })}
                </time>
                {post.frontmatter.tags?.[0] && (
                  <>
                    <span className={`w-px h-3 ${currentTextColor === 'light' ? 'bg-white/50' : 'bg-[#151515]/50'}`}></span>
                    <span className="text-[10px] font-normal tracking-[0.2em] uppercase opacity-90">
                      {post.frontmatter.tags[0]}
                    </span>
                  </>
                )}
              </div>

              {/* Title - Left Aligned, Bold with Slide Up Animation */}
              <h2
                className={`text-3xl md:text-4xl lg:text-5xl font-heading font-bold leading-tight tracking-tight transition-all duration-700 ease-out ${
                  index === current
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                } ${currentTextColor === 'light' ? 'text-white' : 'text-[#151515]'}`}
                style={{ transitionDelay: index === current ? '400ms' : '0ms' }}
              >
                <Link href={`/posts/${post.slug}`} className="hover:opacity-70 transition-opacity">
                  {post.frontmatter.title}
                </Link>
              </h2>

              {/* Excerpt - Subtle Description with Fade In */}
              {post.frontmatter.summary && (
                <p
                  className={`text-sm md:text-base leading-relaxed max-w-xl font-light transition-all duration-700 ease-out ${
                    index === current
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-6'
                  } ${currentTextColor === 'light' ? 'text-white/80' : 'text-[#151515]/80'}`}
                  style={{ transitionDelay: index === current ? '600ms' : '0ms' }}
                >
                  {post.frontmatter.summary}
                </p>
              )}

              {/* CTA - Outlined Button with Fade In */}
              <div
                className={`pt-2 transition-all duration-700 ease-out ${
                  index === current
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: index === current ? '800ms' : '0ms' }}
              >
                <Link
                  href={`/posts/${post.slug}`}
                  className={`inline-block border text-[10px] font-normal tracking-[0.2em] uppercase px-6 py-2.5 transition-all duration-300 ${
                    currentTextColor === 'light'
                      ? 'border-white text-white hover:bg-white hover:text-[#151515]'
                      : 'border-[#151515] text-[#151515] hover:bg-[#151515] hover:text-white'
                  }`}
                >
                  閱讀更多
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Controls - Clean & Simple */}
      <button
        onClick={prevSlide}
        className={`absolute left-8 md:left-12 lg:left-16 top-1/2 -translate-y-1/2 z-30 p-2 transition-colors opacity-0 group-hover:opacity-100 duration-300 ${
          currentTextColor === 'light' ? 'text-white/70 hover:text-white' : 'text-[#151515]/70 hover:text-[#151515]'
        }`}
      >
        <ChevronLeft size={24} strokeWidth={1.5} />
      </button>

      <button
        onClick={nextSlide}
        className={`absolute right-8 md:right-12 lg:right-16 top-1/2 -translate-y-1/2 z-30 p-2 transition-colors opacity-0 group-hover:opacity-100 duration-300 ${
          currentTextColor === 'light' ? 'text-white/70 hover:text-white' : 'text-[#151515]/70 hover:text-[#151515]'
        }`}
      >
        <ChevronRight size={24} strokeWidth={1.5} />
      </button>

      {/* Pagination Dots - Minimal Lines */}
      <div className="absolute bottom-8 md:bottom-12 right-8 md:right-12 lg:right-16 z-30 flex space-x-2.5">
        {posts.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`h-px transition-all duration-300 ${
              currentTextColor === 'light'
                ? idx === current ? 'bg-white w-7' : 'bg-white/50 w-5 hover:bg-white/70'
                : idx === current ? 'bg-[#151515] w-7' : 'bg-[#151515]/50 w-5 hover:bg-[#151515]/70'
            }`}
          />
        ))}
      </div>

      {/* Hidden canvas for image analysis */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
